import axios from "axios";
// Assuming you have a configuration file like:
// const config = { url: "http://localhost:8080" };
import config from "../../config";

// Define the base URL for the Investor Enquiry API endpoints
const BASE_URL = `${config.url}/api/investor-enquiries`;

const InvestorEnquiryManager = {
    // ------------------- C - CREATE -------------------
    /**
     * Submits a new investor enquiry, handling file uploads.
     * @param {Object} enquiryData - The DTO data (excluding the file).
     * @param {File | null} file - The optional file object (e.g., proposal PDF).
     * @returns {Promise<Object>} The API response data.
     */
    async createEnquiry(enquiryData, file) {
        // Create a new FormData object to handle both JSON and file parts
        const formData = new FormData();

        // Append the enquiry JSON data under the 'enquiry' part
        formData.append('enquiry', JSON.stringify(enquiryData));

        // Append the file data under the 'file' part if it exists
        if (file) {
            formData.append('file', file);
        }

        const response = await axios.post(
            `${BASE_URL}/create`, 
            formData, 
            {
                headers: {
                    // Axios will automatically set the correct Content-Type: multipart/form-data
                    // with the boundary when a FormData object is passed.
                    'Content-Type': 'multipart/form-data' 
                }
            }
        );
        return response.data;
    },

    // ------------------- R - READ -------------------

    /**
     * Get all investor enquiries.
     * @returns {Promise<Object>} The API response data containing a list of DTOs.
     */
    async getAllEnquiries() {
        const response = await axios.get(`${BASE_URL}/get`);
        return response.data;
    },

    /**
     * Get a single enquiry by ID.
     * @param {number | string} id - The ID of the enquiry.
     * @returns {Promise<Object>} The API response data containing the DTO.
     */
    async getEnquiryById(id) {
        const response = await axios.get(`${BASE_URL}/get/${id}`);
        return response.data;
    },

    // ------------------- U - UPDATE -------------------

    /**
     * Updates an existing investor enquiry, handling file uploads.
     * @param {number | string} id - The ID of the enquiry to update.
     * @param {Object} enquiryData - The DTO data (excluding the file).
     * @param {File | null} file - The optional new file object.
     * @returns {Promise<Object>} The API response data.
     */
    async updateEnquiry(id, enquiryData, file) {
        const formData = new FormData();
        
        // Append JSON data
        formData.append('enquiry', JSON.stringify(enquiryData));

        // Append the file data if it exists
        if (file) {
            formData.append('file', file);
        }

        const response = await axios.put(
            `${BASE_URL}/${id}`, 
            formData, 
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response.data;
    },

    // ------------------- D - DELETE -------------------

    /**
     * Deletes an enquiry by ID.
     * @param {number | string} id - The ID of the enquiry to delete.
     * @returns {Promise<Object>} The API response data.
     */
    async deleteEnquiry(id) {
        const response = await axios.delete(`${BASE_URL}/${id}`);
        return response.data;
    },

    // ------------------- SEARCH -------------------

    /**
     * Search enquiries by full name.
     * @param {string} name - The full name (or part of the name) to search for.
     * @returns {Promise<Object>} The API response data containing a list of DTOs.
     */
    async searchEnquiriesByFullName(name) {
        // Use encodeURIComponent to safely handle special characters in the name
        const response = await axios.get(`${BASE_URL}/search?name=${encodeURIComponent(name)}`);
        return response.data;
    },
};

export default InvestorEnquiryManager;